import React, { useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { HeaderSearchBar as SharedHeaderSearchBar } from "@tailormed/common-client/component/HeaderSearchBar/HeaderSearchBar";
import { selectFirstRecentPatients } from "@tailormed/common-client/store/reselectSelector";

import { selectSearchedPatients, selectIsFetching, selectAssigneesAndUserGroups } from "../../store/selector";
import ACTIONS from "../../store/action";
import { ApiNames } from "../../api/api";
import { Routes } from "../../constant/routes";
import { useGetSetting } from "../../hooks/getSetting";

const selectSearchFetching = selectIsFetching([ApiNames.PhiSearchFreeText, ApiNames.SearchFreeText]);
const selectRecentFetching = selectIsFetching([ApiNames.getRecentPatients]);

const SEARCH_INPUT_ID = "main_header_search_patient";

// FNC adapter over the shared HeaderSearchBar core.
const HeaderSearchBar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const isSearchFetching = useSelector(selectSearchFetching);
  const isRecentPatientsFetching = useSelector(selectRecentFetching);
  const searchedPatientsStore = useSelector(selectSearchedPatients);
  const firstRecentPatients = useSelector(selectFirstRecentPatients);
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const assigneesById = useMemo(() => new Map(assignees.map((a) => [String(a.id), a])), [assignees]);

  // Core contract normalizes the store shape to { patients, total }.
  const searchedPatients = useMemo(
    () =>
      searchedPatientsStore && {
        ...searchedPatientsStore,
        total: searchedPatientsStore.totalPatients ?? searchedPatientsStore.patients?.length
      },
    [searchedPatientsStore]
  );

  const searchPatients = (page, text, scope) => dispatch(ACTIONS.searchPatientsAction(page, text, scope));
  const getRecentPatients = (page) => dispatch(ACTIONS.getRecentPatientsAction(page, true));
  const clearSearchResults = () => dispatch(ACTIONS.clearSearchedPatients());
  const setSearchResultPage = (wasSearchResultPage, scope, text) =>
    dispatch(ACTIONS.setSearchResultPage(wasSearchResultPage, scope, text));
  const setRecentPatientPage = (wasRecentPatientPage) => dispatch(ACTIONS.setRecentPatientPage(wasRecentPatientPage));
  const shouldReturnToRelevantPatient = (shouldReturn, notificationId) =>
    dispatch(ACTIONS.setShouldReturnToRelevantPatient(shouldReturn, notificationId));
  const setPreviousPage = (previousPage) => dispatch(ACTIONS.setPreviousPage(previousPage));

  // Back-button plumbing flags — only read inside the location effect.
  const previousPageSearchRef = useRef(false);
  const previousPageRecentPatientRef = useRef(false);
  const currentLocationRef = useRef({});

  const normalizeRow = useCallback(
    (raw) => ({
      key: String(raw.patientId),
      patientId: raw.patientId,
      patient: raw,
      facilities: (raw.facilities || []).map((facility) => facility?.name).filter(Boolean),
      viewedAt: raw.lastViewedAt || raw.viewedAt || raw.lastViewed,
      createdAt: raw.createdAt || raw.creationDate || raw.created_at,
      updatedAt: raw.updatedAt || raw.updated_at,
      raw
    }),
    []
  );

  const getAssigneeNames = (norm) =>
    (norm.raw.assignees || [])
      .map((pa) => {
        const assignee = assigneesById.get(String(pa.assignee));
        return assignee ? pa.userName || pa.assigneeName || assignee.displayName : null;
      })
      .filter(Boolean)
      .join(" | ");

  const getExtraMetaParts = (norm) => [{ text: getAssigneeNames(norm), highlight: false }];

  const getExtraTooltipFields = (norm) => {
    const assigneeNames = getAssigneeNames(norm);
    return assigneeNames ? [{ label: t("header_searchbar.tooltip_label.assignee"), value: assigneeNames }] : [];
  };

  const onSelectRow = (raw) => {
    clearSearchResults();
    // Archived patients have no journeyId — route by patientId into the restore flow; active patients keep the journey deep-link.
    if (raw.isArchived) {
      history.push(Routes.PATIENT_PATH({ patientId: raw.patientId }), { isArchived: true });
    } else {
      history.push(Routes.PATIENT_JOURNEY_PATH({ patientId: raw.patientId, journeyId: raw.journeyId }));
    }
  };

  const goToSearchResults = (searchBy, searchText) => {
    if (history.location.pathname.includes(Routes.SEARCH_RESULTS)) {
      history.replace(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText });
    } else {
      history.push(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText });
    }
  };

  return (
    <SharedHeaderSearchBar
      inputId={SEARCH_INPUT_ID}
      useGetSetting={useGetSetting}
      searchedPatients={searchedPatients}
      recentPatients={firstRecentPatients}
      isSearchFetching={isSearchFetching}
      isRecentPatientsFetching={isRecentPatientsFetching}
      searchPatients={searchPatients}
      getRecentPatients={getRecentPatients}
      clearSearchResults={clearSearchResults}
      isSearchResultsPage={(location) => location.pathname.includes(Routes.SEARCH_RESULTS)}
      replaceSearchResultsUrl={(searchBy, searchText) =>
        history.replace(`${Routes.SEARCH_RESULTS}?searchBy=${searchBy}`, { searchText })
      }
      goToSearchResults={goToSearchResults}
      goToRecentPatients={() => history.push(`${Routes.RECENT_PATIENTS}`)}
      normalizeRow={normalizeRow}
      getExtraMetaParts={getExtraMetaParts}
      getExtraTooltipFields={getExtraTooltipFields}
      onSelectRow={onSelectRow}
      locationEffect={({
        location,
        searchTextParams,
        searchByParams,
        isResultsPage,
        searchText,
        searchBy,
        setSearchText,
        setSearchBy
      }) => {
        // Set search text, as well as set variables for the back button on patient details
        // to properly go back to the search results page
        if (isResultsPage && searchText) {
          setSearchText(searchTextParams);
          setSearchBy(searchByParams);

          setSearchResultPage(true, searchBy, searchText);
          previousPageSearchRef.current = true;
        } else if (previousPageSearchRef.current) {
          previousPageSearchRef.current = false;
        } else {
          setSearchResultPage(false, "", "");
        }

        // Set variables for the back button on patient details to properly go back to the recent patients page
        const RECENT_PATIENT_PAGE = location.pathname.includes(Routes.RECENT_PATIENTS);
        if (RECENT_PATIENT_PAGE) {
          setRecentPatientPage(true);
          previousPageRecentPatientRef.current = true;
        } else if (previousPageRecentPatientRef.current) {
          previousPageRecentPatientRef.current = false;
        } else {
          setRecentPatientPage(false);
        }

        // Set variables for the back button on patient details and recent patients page to properly go back to the relevant patients page.
        // Preserve the flag while the user stays on a /patient/* page so intra-patient history.replace
        // calls (e.g. PatientPotentialSavingsList auto-select) don't clear it.
        const TOP_OPPORTUNITIES_PAGE = location.pathname.includes(Routes.TOP_OPPORTUNITIES);
        const PATIENT_PAGE = location.pathname.split("/")[1] === "patient";
        if (TOP_OPPORTUNITIES_PAGE) {
          shouldReturnToRelevantPatient(true, location.pathname.split("/")[2]);
        } else if (!PATIENT_PAGE) {
          shouldReturnToRelevantPatient(false, null);
        }

        // Set current and previous location for back buttons
        if (!isResultsPage || (isResultsPage && searchText)) {
          setPreviousPage(currentLocationRef.current);
          currentLocationRef.current = location;
        }
      }}
    />
  );
};

export { HeaderSearchBar };
